<template>
	<template v-if="!isContentBlocked">
		<div class="edit-row--single edit-row--b-top">
			<loading-el v-if="loading"></loading-el>
			<editor
				:api-key="tinyApiKey"
				:disabled="isContentBlocked"
				v-model="content"
				:init="tinySettings"
        model-events="change keyup undo redo"
			/>
			<div class="flex flex--justify-between flex--align-center pt--10">
				<div class="field-row">
					<field-select
						:options="CONTENT_BAR_OPTIONS"
						:label="t('popup.content.label.typeShort')"
						:label-inline="true"
						:disabled="isContentBlocked"
						:field-classname="'mod--edit-bar'"
						v-model:selected="point.content.popup_type"
            @change="editorRebuild"
						:placeholder="t('objectTemplate.placeholder')"
						:by-value="'value'"
						:property="'name'"
					></field-select>
				</div>
				<div class="button-wrapper flex--grow-2 flex--justify-end">
					<point-source-documents-menu
						v-if="hasContentLlm"
						:map-id="currentMapId"
						:can-attach="isContentLlmAvailable"
						:node-id="currentNode?.id ?? null"
					></point-source-documents-menu>
					<div
						@click="openContentForm"
						class="button button--plain button--h-25 mr--15 bg--white color--black font-weight--500"
					>{{ t('quickEditBar.moreOptions') }}</div>
          <div class="rel">
            <div
                @click="savePoint"
                :class="isSaveAvailable"
                class="button button--icon button--h-25"
            >
              <i class="fas fa-save"></i>
            </div>
            <dialog-inline
                :position="'end'"
                :direction="'top'"
                :active="isQuickEditBarContentWarningActive"
                @confirm="savePointConfirm"
                @cancel="savePointCancel"
            >
	            <p v-html="t('quickEditBar.contentNotSaved')"></p>
            </dialog-inline>
          </div>
				</div>
			</div>
		</div>
		<popup-media
			:popup-status="isMediaContentActive"
			@chosen-media="getChosenMedia"
			@popup-close="popupClose"
		></popup-media>
		<popup-quiz
			:popup-status="isQuizPopupActive"
			@chosen-quiz="getChosenQuiz"
			@popup-close="popupQuizClose"
		></popup-quiz>
	</template>
	<div class="edit-row edit-row--b-top" v-else>
		<div class="edit-row__message">
			<i class="fas fa-exclamation-triangle"></i>
			<span>{{ t('quickEditBar.linkBlocked') }}</span>
		</div>
	</div>
</template>

<script setup>
import {useI18n} from 'vue-i18n';
import {useStore} from 'vuex';
import {computed, inject, nextTick, ref, watch} from 'vue';
import Editor from '@tinymce/tinymce-vue';
import FieldSelect from '@/components/fields/FieldSelect';
import PopupMedia from '@/components/popup/PopupMedia';
import {DASHBOARD_OPTIONS} from '@/models/DASHBOARD_OPTIONS';
import deepIsEqual from '@/utils/deepIsEqual';
import useLoading from '@/utils/Loading';
import LoadingEl from '@/components/ui/loading/LoadingEl.vue';
import DialogInline from "@/components/ui/dialog/DialogInline.vue";
import PermissionService from "@/utils/PermissionService";
import {Roles} from "@/models/Roles";
import PopupQuiz from "@/components/popup/PopupQuiz";
import useTinyMCE from "@/utils/editor/tinymce/useTinyMCE";
import {kontextlabLink} from "@/modules/editor-link";
import useTranslations from "@/utils/language/useTranslations";
import FontsUtils from "@/services/canvas/fonts/Fonts";
import {CONTENT_BAR_OPTIONS} from "@/models/node/CONTENT_BAR_OPTIONS";
import useContentLlm from "@/utils/editor/content/useContentLlm";
import {PointSourceDocumentsMenu} from "@/modules/source-documents";

const {t} = useI18n();
const store = useStore();
const language = computed(() => store.getters["maps/languages/getLanguage"].id);
const currentLang = computed(() => store.getters["maps/languages/getLanguageLocal"].id);
const {getSubvariants, replaceLang} = useTranslations();

// loading
const {loading, loadingShow, loadingHide} = useLoading();

// USER PERMISSION
const userRole = computed(() => store.getters["user/getCurrentUserRole"]);
const quizPermission = computed(() => PermissionService.permissionCheck({userRole: userRole.value, permission: Roles.Editor}));

// CLIENT RESTRICTIONS
const clientFeatures = computed(() => store.getters["clients/getClientFeatures"]);
const clientRestrictions = computed(() => store.getters["clients/getRestrictions"]);
const hasAssetManagement = computed(() => clientRestrictions.value.hasAssetManagement.value);
const hasMediaTemplates = computed(() => clientRestrictions.value.hasMediaTemplates.value);
const isMediaAvailable = computed(() => hasAssetManagement.value || hasMediaTemplates.value);
const hasQuizAccess = computed(() => clientRestrictions.value.hasQuizzes.value);
const isQuizAvailable = computed(() => hasQuizAccess.value && quizPermission.value);
const isI18nAvailable = computed(() => clientFeatures.value.has_i18n);
// Shown when the tenant is entitled to AI content generation; usable only once
// an LLM is connected — until then disabled, saying why.
const hasContentLlm = computed(() => clientFeatures.value.has_content_llm);
const isContentLlmAvailable = computed(() => hasContentLlm.value && clientFeatures.value.content_llm_set);

// MEDIA POPUP
const editorActiveId = computed(() => store.getters['mediaContent/getEditorActive']);
const isMediaContentActive = computed(() => store.getters['mediaContent/isMediaContentActive']);
function getChosenMedia(mediaURL){
  // provide mediaURL to tinyMCE instance
  appendMediaContent({url: mediaURL, editorID: editorActiveId.value, callback: resetEditorActive, sameCaller: true})
  popupClose();
  store.dispatch('settingsPopup/imageVideoModeSet', false);
}
function popupClose(){
  mediaContentToggle(false);
  store.dispatch('settingsPopup/mediaChoosingToggle', false);
}
function mediaContentToggle(status){
  store.dispatch('mediaContent/mediaContentToggle', status);
}

// QUIZ POPUP
const isQuizPopupActive = computed(() => store.getters["quizzes/isQuizChoosing"]);
function getChosenQuiz({id, url, title}){
  appendQuizContent({id, url, title, editorID: editorActiveId.value, callback: resetEditorActive, sameCaller: true});
  popupQuizClose();
}
function popupQuizClose(){
  quizChoosingSet(false);
}
function quizChoosingSet(status){
  store.dispatch('quizzes/quizChoosingSet', status);
}

// CONTENT ITEM
function nodeUpdateContent(content){
  const id = currentNode.value.id;
  store.dispatch('nodes/content/nodeUpdateContent', {id, content});
}
async function nodeSaveContent(content){
  const id = currentNode.value.id;
  return await store.dispatch('nodes/content/nodeSaveContent', {id, content});
}

const content = computed({
  get(){return point.value.content.lang[currentLang.value].content || ''},
  set(value){
    if(value === "") value = null;
    point.value.content.lang[currentLang.value].content = value;
    // updated edit-bar content tracked status
    updateQuickEditBarContentTrack(isChangesTracked.value);
    // remove warning if no changes detected in content
    if(value && !isChangesTracked.value && isQuickEditBarContentWarningActive.value) updateQuickEditBarContentWarning(false);
  }
})
function contentSiblingsLangAdjust(){
  // if the content of the default language has changed,
  // update all non-empty non-translated additional languages
  if(point.value.content.lang[language.value].content !== currentNodeOriginal.value.content.lang[language.value].content){
    const langObj = point.value.content.lang;
    const value = point.value.content.lang[currentLang.value].content;
    for(const [lang, object] of Object.entries(langObj)){
      if(lang !== currentLang.value && !object['content'] && !object.translated){
        langObj[lang].content = replaceLang(value, lang);
      }
    }
  }
  // if the current lang isn't a default language,
  // check if there are subvariants of the current language and update them accordingly
  const subvariants = getSubvariants({id: currentLang.value, lang: point.value.content.lang});
  if(subvariants.length && currentLang.value !== language.value){
    subvariants.forEach(lang => {
      const value = point.value.content.lang[currentLang.value].content;
      if(!point.value.content.lang[lang]['content'] && !point.value.content.lang[lang].translated){
        point.value.content.lang[lang]['content'] = replaceLang(value, lang);
      }
    })
  }
}

const nodeActiveID = computed(() => store.getters["nodes/getSelectedNodeId"]);
const nodes = computed(() => store.getters['nodes/getNodes']);
const currentNode = computed(() => nodes.value.find(node => node.id === nodeActiveID.value));
const isContentBlocked = computed(() => currentNode.value.contentBlocked);

const CONTENT_FORM = DASHBOARD_OPTIONS.CREATE.GROUPS.NODE.CONTENT_FORM;
function openContentForm(){
  store.dispatch("dashboardOptions/optionChoose", CONTENT_FORM);
}

const nodesOriginal = computed(() => store.getters['nodes/getRawNodes']);
const currentNodeOriginal = computed(() => nodesOriginal.value.find(nodeOriginal => nodeOriginal.id === nodeActiveID.value));
const point = ref(JSON.parse(JSON.stringify(currentNodeOriginal.value)));

watch(currentNodeOriginal.value.content, (value, oldValue) => {
  point.value.content = JSON.parse(JSON.stringify(value));
})

// track changes
const isChangesTracked = computed(() =>
    point.value && currentNodeOriginal.value ?
        !deepIsEqual(point.value?.content, currentNodeOriginal.value?.content) :
        false
);
// edit-bar content status
const quickEditBarContentTracked = computed(() => store.getters['nodes/content/getQuickEditBarContentTrackedChanges']);
function updateQuickEditBarContentTrack(status){
  store.dispatch('nodes/content/updateQuickEditBarContentTrack', status);
}

const isQuickEditBarContentWarningActive = computed(() => store.getters['nodes/content/isQuickEditBarContentWarningActive']);
function updateQuickEditBarContentWarning(status){
  store.dispatch('nodes/content/updateQuickEditBarContentWarning', status);
}

// fonts utils
const {getFontProperty, fontFamilyGetNameByUrl} = FontsUtils();
const fonts = computed(() => store.getters['fonts/getFontsCollected']);
function composeFontStyles(settings) {
  if(!settings.font_url) return '';
  const fontSize = settings.font_size || '';
  const lineHeight = settings.line_height || '';
  const fontFamily = fontFamilyGetNameByUrl(settings.font_url, clientFonts.value);
  const {fontWeight, fontStyle} = getFontProperty({fonts, fontValue: fontFamily})
  let result = `
       font-family: ${fontFamily}, sans-serif;
       font-weight: ${fontWeight};
      `
  if(lineHeight) result += `line-height: ${lineHeight}; `;
  if(fontSize) result += `font-size: ${fontSize}px; `;

  return result;
}

// TINY SETTINGS
const clientFonts = computed(() => store.getters["fonts/getClientFonts"]);
const popupGlobalDesign = computed(() => store.getters["design/getContentGlobalDesign"]);
const currentPopupType = computed(() => CONTENT_BAR_OPTIONS.find(option => option.value === point.value.content.popup_type).popup);
const paragraphSpacing = computed(() => popupGlobalDesign.value[currentPopupType.value].paragraph_spacing || 0);
const fontGlobalStyles = computed(() => popupGlobalDesign.value[currentPopupType.value].font_url ? composeFontStyles(popupGlobalDesign.value[currentPopupType.value]) : '');
const editorContent = ref(null);
const {
  tinyApiKey,
  tinyBaseSettings,
  kontextlabAssets,
  kontextlabAccordion,
  kontextlabQuiz,
  kontextlabKaia,
  langLink,
  appendMediaContent,
  appendQuizContent
} = useTinyMCE(clientFonts.value);

// Generate article content for the current point via the tenant's own LLM.
// Returns an HTML fragment the KAIA toolbar button inserts at the cursor.
const {generateNodeContent: generateContentLlm} = useContentLlm();
const currentMapId = computed(() => store.getters['maps/getCurrentMapId']);
function generateNodeContent(){
  const title = currentNode.value?.title
    || point.value?.content?.lang?.[currentLang.value]?.title
    || '';
  return generateContentLlm({
    mapId: currentMapId.value,
    nodeId: currentNode.value?.id,
    title,
    language: currentLang.value
  });
}

const tinySettings = {
  ...tinyBaseSettings,
  content_style: `p {margin-bottom: ${paragraphSpacing.value}em; ${fontGlobalStyles.value}}`,
  height: 250,
  menubar: false,
  toolbar: 'bold italic underline | forecolor | numlist bullist accordionk | link code | media-assets image media quiz lang-link | kaia',
  setup: editor => {
    editorContent.value = editor;
    kontextlabLink(editor);
    kontextlabAccordion(editor);
    if(hasContentLlm.value) kontextlabKaia(editor, {
      generate: generateNodeContent,
      available: isContentLlmAvailable.value,
      messages: {
        tooltip: t('quickEditBar.kaia.tooltip'),
        unavailable: t('quickEditBar.kaia.unavailable'),
        failed: t('quickEditBar.kaia.failed')
      }
    });
    if(isMediaAvailable.value) kontextlabAssets(editor);
    if(isQuizAvailable.value) kontextlabQuiz(editor);
    if(isI18nAvailable.value) langLink(editor);
  }
}

function editorRebuild(value){
  nextTick(() => {
    editorContent.value.iframeElement.contentDocument.getElementsByTagName('style')[0].innerHTML = `.mce-content-body p {margin-bottom: ${paragraphSpacing.value}em; ${fontGlobalStyles.value}}`;
    updateQuickEditBarContentTrack(isChangesTracked.value);
  })
}

// save changes
const isSaveAvailable = computed(() => !isChangesTracked.value ? 'disabled bg--black' : 'bg--pink');

async function savePoint(){
  loadingShow();
  // save point to server
  if(currentNodeOriginal.value.occupied) return;
  contentSiblingsLangAdjust();
  try{
    await nodeUpdateContent(point.value.content);
    const updatedNode = await nodeSaveContent(point.value.content);
    // update node with new data
    await nodeUpdateContent(updatedNode.content); // TODO - improve to call nodeUpdateContent just once?

    await updateQuickEditBarContentTrack(false);
    await updateQuickEditBarContentWarning(false);
    loadingHide();
  } catch(err){
    loadingHide();
  }
}

// ACTIONS
function resetEditorActive(){
  store.dispatch('mediaContent/setEditorActive', null);
}
function optionChoose(option){
  store.dispatch('nodes/setSelectedNode', option);
}
function setSelectedNode(nodeId){
  store.dispatch('nodes/setSelectedNode', nodeId);
}
function nodeGroupToggle(status){
  store.dispatch('dashboardOptions/groupNodeToggle', status);
}
function stopEditingNode(){
  store.dispatch('simultaneousEditing/stopEditingNode')
}

function savePointConfirm(){
  updateQuickEditBarContentTrack(false);
  updateQuickEditBarContentWarning(false);
  savePoint();
}
function savePointCancel(){
  updateQuickEditBarContentTrack(false);
  updateQuickEditBarContentWarning(false);

  nodeGroupToggle(false);
  setSelectedNode(null);
  // unselect node manually
  const node = nodes.value.find(node => node.id === point.value?.id)
  if(node) node.setSelected(false);
  stopEditingNode();
}

</script>

<style lang="scss">

</style>
